import React, { useEffect, useState } from "react";
import { useCompanies } from "../../hooks/useCompanies";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useSession } from "../../context/SessionContext";
import { Company, CompanyUpdatePayload } from "../../types/company.types";
import { capitalizeFullName, formatCnpj, formatPhoneBR, normalizeForCompare } from "../../lib/formatters";
import { Button, Input, notify } from "../ui";
import { SuggestInput } from "../patterns/SuggestInput";
import { t } from "../../i18n";
import CompanyEditModal from "./CompanyEditModal";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

interface CompanyLinkFormProps {
  existingCompanies: Company[];
  onLinked: (company: Company) => void;
  submitLabel?: string;
  // Quando true, o e-mail deixa de ser opcional — usado no fluxo de envio por
  // email, onde a empresa só pode ser selecionada como destinatária se tiver
  // um e-mail cadastrado.
  requireEmail?: boolean;
}
const CompanyLinkForm: React.FC<CompanyLinkFormProps> = ({
  existingCompanies,
  onLinked,
  submitLabel = t("links.company.submit"),
  requireEmail = false,
}) => {
  const { searchCompanies, createCompany, updateCompany, linkCompany, searching, creating, updating, linking } =
    useCompanies();
  const { setMetadata } = useSession();

  const [nameInput, setNameInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [cnpjInput, setCnpjInput] = useState("");
  const [suggestions, setSuggestions] = useState<Company[]>([]);

  // Empresa clicada nas sugestões (já existe no sistema) — aguardando
  // confirmação de vínculo (e, opcionalmente, complemento de dados) no modal.
  const [companyToReview, setCompanyToReview] = useState<Company | null>(null);

  const debouncedName = useDebouncedValue(nameInput, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const query = debouncedName.trim();

    // `debouncedName` só reflete o texto atual depois que o delay termina.
    // Sem esta checagem, dispararíamos uma busca fantasma para um texto
    // que o usuário já apagou/trocou antes do debounce terminar.
    if (query !== nameInput.trim() || query.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    searchCompanies(query).then((result) => {
      if (!cancelled && result) setSuggestions(result);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName]);

  const isAlreadyLinked = (companyId: number) => existingCompanies.some((c) => c.id === companyId);

  const handleNameBlur = () => {
    if (nameInput.trim()) {
      setNameInput(capitalizeFullName(nameInput.trim()));
    }
  };

  // Empresa já existe no sistema — em vez de vincular direto, abre o modal
  // de conferência/complemento de dados (ver `handleReviewConfirm`).
  const handleSelectSuggestion = (company: Company) => {
    setSuggestions([]);
    setCompanyToReview(company);
  };

  const clearForm = () => {
    setNameInput("");
    setPhoneInput("");
    setEmailInput("");
    setCnpjInput("");
    setSuggestions([]);
  };

  const linkAndSync = async (company: Company) => {
    // linkCompany devolve `void` em caso de sucesso (undefined) e `null` em caso de erro,
    // então a checagem precisa ser estrita — `!result` marcaria sucesso como falha.
    const result = await linkCompany(company.id);
    if (result === null) return false;

    // `metadata.companies` é lido em outras telas (ex: ProtectedRoute/CallBackAuth
    // populam isso no login) — sem atualizar aqui, essas telas ficariam com a lista
    // desatualizada até o médico deslogar e logar de novo.
    setMetadata((prev) => ({ ...prev, companies: [...prev.companies, company] }));
    onLinked(company);
    return true;
  };

  const handleAdd = async () => {
    const trimmedName = nameInput.trim();

    if (!trimmedName) {
      notify({ tone: "error", title: t("links.company.nameRequired") });
      return;
    }

    if (existingCompanies.some((c) => c.name.toLowerCase() === trimmedName.toLowerCase())) {
      notify({ tone: "error", title: t("links.company.alreadyLinked") });
      return;
    }

    if (requireEmail && !emailInput.trim()) {
      notify({ tone: "error", title: t("links.company.emailRequired") });
      return;
    }

    const created = await createCompany({
      name:  capitalizeFullName(trimmedName),
      phone: phoneInput.trim() || undefined,
      email: emailInput.trim().toLowerCase() || undefined,
      cnpj:  cnpjInput.trim() || undefined,
    });
    // erro já foi mostrado via toast pelo useApi (ex: CNPJ inválido)
    if (!created) return;

    if (await linkAndSync(created)) clearForm();
  };

  // Confirmação vinda do modal de revisão (clique numa sugestão já existente):
  // `payload` só tem os campos que o médico preencheu a mais — vazio quando
  // ele optou por "Apenas vincular" ou quando a empresa já tinha tudo preenchido.
  const handleReviewConfirm = async (payload: CompanyUpdatePayload) => {
    if (!companyToReview) return;

    let companyToLink = companyToReview;

    if (Object.keys(payload).length > 0) {
      const updated = await updateCompany(companyToReview.id, payload);
      // erro (ex: CNPJ inválido) já foi mostrado via toast — mantém o modal aberto para corrigir
      if (!updated) return;
      companyToLink = updated;
    }

    if (await linkAndSync(companyToLink)) setCompanyToReview(null);
  };

  const isSubmitting = creating || linking;

  // `suggestions` só é atualizado quando a busca debounced resolve — sem este
  // filtro, uma sugestão buscada para um texto anterior (ex: "Empresa teste")
  // continuaria clicável mesmo depois do médico já ter digitado algo que não
  // bate mais com ela (ex: "Empresa teste 3"), permitindo vincular a empresa
  // errada por engano enquanto o texto digitado nunca chega a ser enviado.
  const normalizedQuery = normalizeForCompare(nameInput.trim());
  const visibleSuggestions = suggestions.filter(
    (s) => !isAlreadyLinked(s.id) && normalizeForCompare(s.name).includes(normalizedQuery),
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      {companyToReview && (
        <CompanyEditModal
          company={companyToReview}
          saving={updating || linking}
          onConfirm={handleReviewConfirm}
          onClose={() => setCompanyToReview(null)}
          requireEmail={requireEmail}
        />
      )}

      <SuggestInput
        value={nameInput}
        onChange={setNameInput}
        onBlur={handleNameBlur}
        onEnter={handleAdd}
        placeholder={t("links.company.namePlaceholder")}
        disabled={isSubmitting}
        searching={searching}
        suggestions={visibleSuggestions}
        getKey={(company) => company.id}
        onSelect={handleSelectSuggestion}
        renderSuggestion={(company) => (
          <>
            <span className="font-medium">{company.name}</span>
            {company.cnpj && <span className="block text-caption text-fg-subtle">{company.cnpj}</span>}
          </>
        )}
      />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-2">
        <Input
          type="text"
          aria-label={t("links.company.phonePlaceholder")}
          placeholder={t("links.company.phonePlaceholder")}
          value={phoneInput}
          disabled={isSubmitting}
          onChange={(e) => setPhoneInput((prev) => formatPhoneBR(e.target.value, prev))}
        />
        <Input
          type="email"
          aria-label={requireEmail ? t("links.company.emailRequiredPlaceholder") : t("links.company.emailPlaceholder")}
          placeholder={requireEmail ? t("links.company.emailRequiredPlaceholder") : t("links.company.emailPlaceholder")}
          value={emailInput}
          disabled={isSubmitting}
          onChange={(e) => setEmailInput(e.target.value.toLowerCase())}
        />
        <Input
          type="text"
          aria-label={t("links.company.cnpjPlaceholder")}
          placeholder={t("links.company.cnpjPlaceholder")}
          value={cnpjInput}
          disabled={isSubmitting}
          onChange={(e) => setCnpjInput(formatCnpj(e.target.value))}
        />
      </div>

      <Button
        variant="primary"
        className="self-start"
        onClick={handleAdd}
        loading={isSubmitting}
        disabled={!nameInput.trim() || (requireEmail && !emailInput.trim())}
      >
        {submitLabel}
      </Button>
    </div>
  );
};

export default CompanyLinkForm;
